// journal — the per-subdomain client module for the Journal: the workspace's
// periods told in prose, the author's own text, and corrections of the
// assistant's. Implements the module contract (modules/module.ts).
//
// Topology (entry file + sibling folder):
//  - journal/model.ts         — JournalModel (page, drafts, optimistic rollback)
//  - journal/msg.ts           — JournalMsg (intents, outcomes)
//  - journal/effect.ts        — JournalEffect (port calls as data)
//  - journal/update.ts        — updateJournal + inboundJournal (pure)
//  - journal/view-model.ts    — projectJournal (pure)
//  - journal/view.tsx         — JournalView (pure React projection)
//  - journal/ChipTextEditor   — the correction editor (links as chips)
//  - journal/run.ts           — the interpreter (the ONE impure file)
//  - journal/memoryPort.ts    — an in-memory JournalPort for tests + stories

import type { JournalEffect } from "./journal/effect.js";
import { initialJournalModel, type JournalModel } from "./journal/model.js";
import type { JournalMsg } from "./journal/msg.js";
import { type JournalDeps, mkRunJournal } from "./journal/run.js";
import {
    inboundJournal,
    type JournalRefusal,
    updateJournal,
} from "./journal/update.js";
import type { Module } from "./module.js";

export type { JournalModel } from "./journal/model.js";
export type { JournalMsg } from "./journal/msg.js";
export type { DraftStash, JournalDeps } from "./journal/run.js";
export { JournalView } from "./journal/view.js";
export { projectJournal, projectYourTexts } from "./journal/view-model.js";
export { YourTexts } from "./journal/yourTexts.js";

export type JournalModule = Module<
    JournalModel,
    JournalMsg,
    JournalEffect,
    JournalRefusal
>;

export const mkJournalModule = (deps: JournalDeps): JournalModule => ({
    init: initialJournalModel,
    update: updateJournal,
    run: mkRunJournal(deps),
    inbound: inboundJournal,
});
